import type { AgentsSummaryResponse } from '@kanbu/schema/contracts';
import { DocumentStatus } from '@kanbu/schema/enums';
import { tryCatchSync } from '@kanbu/shared';
import { Trans } from '@lingui/react/macro';
import { ChevronRightIcon, Loader2Icon } from 'lucide-react';

import { AppSettings } from '@/constants';
import { cn } from '@/lib';
import {
  Accordion,
  AccordionTrigger,
  AccordionItem,
  AccordionContent,
} from '@/ui/accordion';
import { Button } from '@/ui/button';

import { AccordionIdentifies } from './accordion-identifiers';
import { Badge } from '../../badge/badge';
import { UrlList, UrlListItem } from '../../url-list/url-list';

export interface KnownSourcesAccordionProps {
  summary: AgentsSummaryResponse | undefined;
  chatId: string;
  onValueChange: (value: string[]) => void;
  value: string[];
}

export function KnownSourcesAccordion({
  summary,
  chatId,
  onValueChange,
  value,
}: KnownSourcesAccordionProps) {
  const totalCount = summary?.documents.length ?? 0;
  const doneCount =
    summary?.documents.filter(d => d.status === DocumentStatus.Done).length ??
    0;
  const percentageDone = Math.round((doneCount / totalCount) * 100);

  return (
    <Accordion type='multiple' value={value} onValueChange={onValueChange}>
      <AccordionItem value={AccordionIdentifies.Sources}>
        <AccordionTrigger>
          <div className='flex flex-col gap-1'>
            <div className='flex flex-row gap-1 items-baseline'>
              <Trans>Sources I know</Trans>
              <span className='text-muted-foreground text-xs'>
                {doneCount}/{totalCount}
              </span>
            </div>
            {percentageDone >= 100 ? (
              <Badge variant='success'>
                <Trans>Your chatbot has already learned your sources!</Trans>
              </Badge>
            ) : percentageDone > 60 ? (
              <Badge variant='success'>
                <Trans>
                  Your chatbot has already learned most of your sources!
                </Trans>
              </Badge>
            ) : percentageDone > 0 ? (
              <Badge variant='info'>
                <Trans>Your chatbot is currently learning your sources!</Trans>
              </Badge>
            ) : null}
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <UrlList
            itemsCount={summary?.documents.length}
            totalCount={summary?.documentsCount}
          >
            {summary?.documents.map(item => {
              const url = item.publicUrl ?? item.uri;
              const [parsedUrl, error] = tryCatchSync(() => {
                const parsedUrl = new URL(url);

                return `${parsedUrl.hostname}${parsedUrl.pathname === '/' ? '' : parsedUrl.pathname}${parsedUrl.search}`;
              });

              if (error) {
                return null;
              }

              const isDone = item.status === DocumentStatus.Done;

              return (
                <UrlListItem
                  key={item.id}
                  url={url}
                  linkClassName={cn(!isDone && 'pr-4 text-muted-foreground')}
                >
                  {item.name?.trim() || parsedUrl}
                  {!isDone && (
                    <span className='absolute right-0 top-0'>
                      <Loader2Icon
                        className='size-2.5 text-primary animate-spin'
                        strokeWidth={2.5}
                      />
                    </span>
                  )}
                </UrlListItem>
              );
            })}
          </UrlList>
          <div className='mt-2'>
            <Button
              data-cta='playground-agent-info-section-add-sources-admin-button'
              variant='outline'
              size='sm'
              asChild
            >
              <a
                href={`${AppSettings.adminAppUrl}/chats/${chatId}/documents`}
                target='_blank'
                rel='noreferrer'
              >
                <Trans>Add sources</Trans>{' '}
                <ChevronRightIcon className='size-4' />
              </a>
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
